import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@leadsnavi/ui-kit';
import cn from 'classnames';

export interface ConfirmOptions {
  title?: string;
  message: string;
  danger?: boolean;
  okText?: ReactNode;
  okIcon?: ReactNode;
  cancelText?: ReactNode;
  showCancel?: boolean;
}

interface ConfirmInstance extends ConfirmOptions {
  id: string;
  resolve: (value: boolean) => void;
  loading: boolean;
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [instances, setInstances] = useState<ConfirmInstance[]>([]);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      const id = Math.random().toString(36).substr(2, 9);
      const instance: ConfirmInstance = {
        ...options,
        id,
        resolve,
        loading: false,
      };
      setInstances(prev => [...prev, instance]);
    });
  }, []);

  const removeInstance = useCallback((id: string) => {
    setInstances(prev => prev.filter(instance => instance.id !== id));
  }, []);

  const setInstanceLoading = useCallback((id: string, loading: boolean) => {
    setInstances(prev => prev.map(instance => (instance.id === id ? { ...instance, loading } : instance)));
  }, []);

  const handleConfirm = useCallback(
    async (instance: ConfirmInstance) => {
      setInstanceLoading(instance.id, true);
      try {
        await instance.resolve(true);
      } finally {
        removeInstance(instance.id);
      }
    },
    [removeInstance, setInstanceLoading]
  );

  const handleCancel = useCallback(
    (instance: ConfirmInstance) => {
      instance.resolve(false);
      removeInstance(instance.id);
    },
    [removeInstance]
  );

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {instances.map(instance => (
        <AlertDialog key={instance.id} open={true} onOpenChange={() => handleCancel(instance)}>
          <AlertDialogContent>
            <AlertDialogHeader className="text-left">
              {instance.title ? <AlertDialogTitle>{instance.title}</AlertDialogTitle> : null}
              <AlertDialogDescription>{instance.message}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              {instance.showCancel && (
                <AlertDialogCancel onClick={() => handleCancel(instance)}>
                  {instance.cancelText || 'Cancel'}
                </AlertDialogCancel>
              )}
              <Button
                className={cn(
                  instance.danger ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''
                )}
                onClick={() => handleConfirm(instance)}
                loading={instance.loading}
              >
                {instance.okIcon && <span className="">{instance.okIcon}</span>}
                {instance.okText || 'Confirm'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ))}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context.confirm;
}
